import { useEffect, useState, useCallback } from "react";

import { createPortal } from "react-dom";

import { useTranslation } from "react-i18next";

import { Monitor, Check, X } from "lucide-react";

interface PairRequestDialogProps {
  hostName: string;

  hostIp: string;

  hostPort: number;

  requestId: string;

  onRespond: (requestId: string, accepted: boolean) => void;
}

const AUTO_REJECT_SECONDS = 30;

export function PairRequestDialog({
  hostName,

  hostIp,

  hostPort,

  requestId,

  onRespond,
}: PairRequestDialogProps) {
  const { t } = useTranslation("streaming");

  const [secondsLeft, setSecondsLeft] = useState(AUTO_REJECT_SECONDS);

  const handleRespond = useCallback(
    (accepted: boolean) => {
      onRespond(requestId, accepted);
    },

    [onRespond, requestId],
  );

  useEffect(() => {
    if (secondsLeft <= 0) {
      handleRespond(false);

      return;
    }

    const interval = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [secondsLeft, handleRespond]);

  // Escape key rejects

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleRespond(false);
    };

    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, [handleRespond]);

  const progress = (secondsLeft / AUTO_REJECT_SECONDS) * 100;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("pairRequestTitle", { defaultValue: "Pair Request" })}
        className="relative w-full max-w-md mx-4 rounded-2xl border border-white/8 bg-card/80 backdrop-blur-xl shadow-2xl overflow-hidden"
      >
        {/* Countdown progress bar */}
        <div className="h-1 w-full bg-white/5">
          <div
            className="h-full bg-purple-500 transition-all duration-1000 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="p-6 space-y-5">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-500/15 border border-purple-500/20">
              <Monitor className="w-5 h-5 text-purple-400" />
            </div>
            <h2 className="text-lg font-semibold text-foreground">
              {t("pairRequestTitle", { defaultValue: "Pair Request" })}
            </h2>
          </div>

          {/* Host info */}
          <div className="space-y-3">
            <p className="text-sm text-foreground/90">
              {t("pairRequestFrom", {
                hostName,

                defaultValue: `Connection request from {{hostName}}`,
              })}
            </p>

            <div className="rounded-lg bg-white/5 border border-white/8 px-4 py-3">
              <p className="text-xs text-muted-foreground">
                {t("pairRequestInfo", {
                  hostIp,

                  hostPort,

                  defaultValue: `{{hostIp}}:{{hostPort}}`,
                })}
              </p>
            </div>
          </div>

          {/* Auto-reject countdown */}
          <p className="text-xs text-muted-foreground text-center">
            {t("autoRejectIn", {
              seconds: secondsLeft,

              defaultValue: `Auto-rejecting in {{seconds}}s`,
            })}
          </p>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => handleRespond(false)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-white/10 text-sm font-medium text-foreground/80 hover:bg-white/5 active:bg-white/10 transition-colors cursor-pointer"
            >
              <X className="w-4 h-4" />
              {t("reject", { defaultValue: "Reject" })}
            </button>

            <button
              type="button"
              onClick={() => handleRespond(true)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-500 active:bg-purple-400 text-sm font-medium text-white transition-colors cursor-pointer"
            >
              <Check className="w-4 h-4" />
              {t("accept", { defaultValue: "Accept" })}
            </button>
          </div>
        </div>
      </div>
    </div>,

    document.body,
  );
}
